import { UmsMember, UmsMemberPointHistoryV2 } from 'types/ums';
import LOCALS from 'commons/locals';
import { Trans } from 'react-i18next';
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Table,
  message,
} from 'antd';
import usePagination from 'commons/hooks/usePagination';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PageQuery } from 'types/base';
import { getMemberPointHistoryListV2, umsMemberPointUpdate } from 'apis/ums';
import { ColumnsType } from 'antd/es/table';
import showTotal from 'components/show-total';
import formatTime from 'utils/formatTime';
import LinkButton from 'components/link-button';
import i18n from 'i18n';
import useResource from 'commons/hooks/useResource';
import { Edit, Gift } from 'lucide-react';
import { useAppSelector } from 'store/hooks';
import { selectUserInfo } from 'store/slices/userInfoSlice';

const MEMBER_UPDATE_POINT_TYPE_MAP = {
  ACTIVITY: 12,
  SYSTEM: 13,
};

const MEMBER_UPDATE_POINT_TYPE_OPTION_LIST = [
  { value: MEMBER_UPDATE_POINT_TYPE_MAP.ACTIVITY, label: 'ACTIVITY' },
  { value: MEMBER_UPDATE_POINT_TYPE_MAP.SYSTEM, label: 'SYSTEM' },
];

type MemberPointHistoryProps = {
  memberId: UmsMember['id'];
};

const MemberPointHistory = ({ memberId }: MemberPointHistoryProps) => {
  const {
    loading,
    setLoading,
    pageNum,
    setPageNum,
    pageSize,
    setPageSize,
    total,
    setTotal,
    dataSource,
    setDataSource,
  } = usePagination<UmsMemberPointHistoryV2>();

  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const memberChangePointResource = useResource('member-change-point');
  const { isAdmin } = useAppSelector(selectUserInfo);

  const onOk = useCallback(async () => {
    const data = await form.validateFields();

    Modal.confirm({
      title: <Trans i18nKey={LOCALS.confirm_submit} />,
      onOk: async () => {
        await umsMemberPointUpdate({
          memberId,
          ...data,
        });

        message.success(i18n.t(LOCALS.successful_operation));
        setTimeout(() => {
          window.location.reload();
        }, 500);
      },
    });
  }, [form, memberId]);

  const getDataSource = useCallback(
    async ({ pageNum, pageSize }: PageQuery) => {
      try {
        setLoading(true);
        const { list, total } = await getMemberPointHistoryListV2({
          memberId,
          pageNum,
          pageSize,
        });

        setDataSource(list);
        setTotal(total);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    },
    [memberId, setDataSource, setLoading, setTotal],
  );

  useEffect(() => {
    getDataSource({ pageNum: 1, pageSize: 10 });
  }, [getDataSource]);

  const getColumns = useCallback(() => {
    return [
      {
        title: <Trans i18nKey={LOCALS.dwawuQNUEi}></Trans>,
        dataIndex: 'orderId',
        key: 'orderId',
        render: (orderId: UmsMemberPointHistoryV2['orderId']) => {
          if (!orderId) return '-';

          return (
            <LinkButton href={`/oms/order-view/${orderId}`}>
              {orderId}
            </LinkButton>
          );
        },
      },
      {
        title: <Trans i18nKey={LOCALS.change_amount}></Trans>,
        dataIndex: 'changeCount',
        key: 'changeCount',
      },
      {
        title: <Trans i18nKey={LOCALS.operator}></Trans>,
        dataIndex: 'operateMan',
        key: 'operateMan',
        render: (operateMan: UmsMemberPointHistoryV2['operateMan']) => {
          return operateMan || '-';
        },
      },
      {
        title: <Trans i18nKey={LOCALS.note}></Trans>,
        dataIndex: 'operateNote',
        key: 'operateNote',
        render: (operateNote: UmsMemberPointHistoryV2['operateNote']) => {
          return operateNote ? (
            <div className="whitespace-pre-wrap">{operateNote}</div>
          ) : (
            '-'
          );
        },
      },
      {
        title: <Trans i18nKey={LOCALS.created_time}></Trans>,
        dataIndex: 'createTime',
        key: 'createTime',
        render: (createTime: UmsMemberPointHistoryV2['createTime']) => {
          return formatTime(createTime);
        },
      },
    ];
  }, []);

  const columns: ColumnsType<UmsMemberPointHistoryV2> = useMemo(
    () => getColumns(),
    [getColumns],
  );

  return (
    <Card bodyStyle={{ padding: '16px' }}>
      <Modal
        title={<Trans i18nKey={LOCALS.modify_points} />}
        open={open}
        onCancel={() => {
          setOpen(false);
        }}
        onOk={onOk}
      >
        <Form form={form} labelCol={{ style: { width: 125 } }}>
          <Form.Item
            name="point"
            label={<Trans i18nKey={LOCALS.change_amount} />}
            initialValue={0}
            rules={[{ required: true }]}
          >
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="type"
            hidden
            label={<Trans i18nKey={LOCALS.type} />}
            initialValue={MEMBER_UPDATE_POINT_TYPE_MAP.SYSTEM}
            rules={[{ required: true }]}
          >
            <Select options={MEMBER_UPDATE_POINT_TYPE_OPTION_LIST} />
          </Form.Item>

          <Form.Item
            name="operateNote"
            label={<Trans i18nKey={LOCALS.remark} />}
            rules={[{ required: true }]}
          >
            <Input.TextArea placeholder={i18n.t(LOCALS.please_enter) || ''} />
          </Form.Item>
        </Form>
      </Modal>

      <div className="flex items-center justify-between mb-4 text-base font-medium">
        <div className="flex items-center gap-2">
          <Gift className="w-5 h-5" />
          <Trans i18nKey={LOCALS.point_history} />
        </div>
        {isAdmin && memberChangePointResource && (
          <Button
            type="primary"
            className="flex items-center gap-2"
            onClick={() => {
              setOpen(true);
            }}
          >
            <Edit className="w-4 h-4" />
            <Trans i18nKey={LOCALS.modify_points} />
          </Button>
        )}
      </div>

      <Table
        bordered
        pagination={{
          showTotal,
          total,
          pageSize,
          current: pageNum,
          onChange: (page, pageSize) => {
            setPageNum(page);
            setPageSize(pageSize);
            getDataSource({ pageNum: page, pageSize });
          },
        }}
        loading={loading}
        rowKey={'id'}
        tableLayout="auto"
        scroll={{ x: 'max-content' }}
        dataSource={dataSource}
        columns={columns}
      />
    </Card>
  );
};

export default MemberPointHistory;
